import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Button from "../../components/controls/button";
import { ApplicationProductDetail } from "../../models/product/application-product-detail";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import {
    fetchByProductId,
    insertBasketItem,
    updateBasketItem,
} from "../../store/features/ordering/basket/basket-slice";
import { useAppSelector } from "../../hooks/useAppSelector";
import { ApplicationBasketItem } from "../../models/order/basket/application-basket-item";
import { minValueValidator, requiredValidator } from "../../helpers/validators";
import { showSuccess } from "../../components/notification/toastr-actions";
import Image from "../../components/controls/image";
import TextInput from "../../components/controls/text-input";
import {
    fetchProduct,
    fetchSupplierProduct,
} from "../../store/features/product/product-slice";
import { ApplicationApprovalStatus } from "../../models/order/application-approval-status";
import {
    FeatureCard,
    FeatureCardColumn,
    FeatureCardRow,
    FeatureCardSingleColumn,
    FeatureCardTitle,
} from "../../styles/shared/card";
import { ApplicationBestSupplierProduct } from "../../models/product/application-best-supplier-product";
import { TextContainer } from "../../styles/shared/table";
import { currencyFormatter } from "../../helpers/formatters";
import { Alert } from "@mui/material";
import { ApplicationSupplierProductHeader } from "../../models/supplier/application-supplier-product-header";
import { fetchProductSuppliers } from "../../store/features/ordering/approval/approval-slice";
import NumberInput from "../../components/controls/number-input";
import { palletFormatter, pluraliseUnit } from "../../helpers/formatters";

interface ProductDrawerProps {
    productId: number;
    branchId: number;
    basketCheck: boolean;
    addToBasketCallback?: () => void;
}

interface ErrorState {
    quantity: string | null;
    currentStock: string | null;
}

export default function ProductDrawer({
    productId,
    branchId,
    basketCheck,
    addToBasketCallback,
}: ProductDrawerProps) {
    const [currentStock, setCurrentStock] = useState<number | null>();
    const [quantity, setQuantity] = useState<number | null>();
    const [notes, setNotes] = useState<string>("");
    const [addToBasketMessage, setAddToBasketMessage] =
        useState<string>("Add to basket");
    const [errors, setErrors] = useState<ErrorState>({
        quantity: null,
        currentStock: null,
    });
    const [basketItem, setBasketItem] = useState<ApplicationBasketItem | null>();
    const basket = useAppSelector((state) => state.basket);
    const dispatch = useAppDispatch();
    const [product, setProduct] = useState<ApplicationProductDetail>();
    const [supplierProduct, setSupplierProduct] =
        useState<ApplicationBestSupplierProduct | null>();
    const [loading, setLoading] = useState(false);
    const [supplierProducts, setSupplierProducts] = useState<ApplicationSupplierProductHeader[]>()
    const submitInFlightRef = useRef(false);
    const stockRef = useRef<HTMLInputElement>(null);
    const quantityRef = useRef<HTMLInputElement>(null);
    const notesRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (productId) {
            setLoading(true);
            dispatch(fetchProduct(productId)).then((response) => {
                var product = response.payload as ApplicationProductDetail;
                setProduct(product);
                resetInputs();
            });

            dispatch(fetchSupplierProduct({ productId, branchId })).then(
                (response) => {
                    var supplierProduct = response.payload as ApplicationBestSupplierProduct;
                    if (supplierProduct.supplierId > 0) {
                        setSupplierProduct(supplierProduct);

                    } else {
                        setSupplierProduct(null);
                    }

                    setLoading(false);
                }
            );
        }
    }, [productId]);

    useEffect(() => {
        if (basketItem) {
            setAddToBasketMessage("Update");
        } else {
            setAddToBasketMessage("Add to Basket");
        }
    }, [basketItem]);

    useEffect(() => {
        if (supplierProduct && branchId && productId) {
            dispatch(fetchProductSuppliers({ branchId, productIds: [productId] }))
                .then(response => {
                    const payload = response.payload as Record<number, ApplicationSupplierProductHeader[]> | ApplicationSupplierProductHeader[] | null;
                    const suppliers = Array.isArray(payload)
                        ? payload
                        : payload?.[productId] ?? [];
                    setSupplierProducts(
                        suppliers.filter(
                            (s) =>
                                s.supplierId !== supplierProduct.supplierId
                        )
                    );
                })
        }
    }, [supplierProduct, branchId, productId])

    useEffect(() => {
        if (basket.basket && product && basketCheck) {
            setLoading(true)
            dispatch(
                fetchByProductId({ basketId: basket.basket.id, productId: product.id })
            ).then((response) => {
                var basketItem = response.payload as ApplicationBasketItem;
                if (basketItem.id > 0 && basketItem.productId === product.id) {
                    setBasketItem(basketItem);
                    setQuantity(basketItem.quantity);
                    setCurrentStock(basketItem.currentStock);
                    setNotes(basketItem.notes);
                } else {
                    setBasketItem(null);
                }
                setLoading(false)
            });
        }
    }, [product]);

    const updateBasket = async () => {
        if (submitInFlightRef.current) {
            return;
        }

        const quantityError = validateQuantity();
        const currentStockError = requiredValidator(currentStock) ?? minValueValidator(currentStock, 0);

        if (!basketItem) {
            return;
        }
        if (quantityError || currentStockError || !product) {
            setErrors({
                ...errors,
                quantity: quantityError,
                currentStock: currentStockError,
            });
            return;
        }

        submitInFlightRef.current = true;
        setLoading(true);
        const update = await dispatch(
            updateBasketItem({
                basketItemId: basketItem.id,
                quantity: quantity!,
                currentStock: currentStock!,
                notes: notes
            })
        );

        if (updateBasketItem.fulfilled.match(update)) {
            resetInputs();
            dispatch(showSuccess(`${product.name} updated`));

            if (addToBasketCallback) {
                addToBasketCallback();
            }
        } else {
            setLoading(false)
        }

        submitInFlightRef.current = false;

    }
    const addToBasket = async () => {
        if (submitInFlightRef.current) {
            return;
        }

        const quantityError = validateQuantity();

        const currentStockError = requiredValidator(currentStock) ?? minValueValidator(currentStock, 0);

        if (!supplierProduct) {
            return;
        }

        if (quantityError || currentStockError || !product) {
            setErrors({
                ...errors,
                quantity: quantityError,
                currentStock: currentStockError,
            });
        } else {
            submitInFlightRef.current = true;
            setLoading(true);
            var basketItem: ApplicationBasketItem = {
                id: 0,
                productId: product.id,
                basketId: basket.basket!.id,
                productName: product.name,
                productDescription: product.description,
                productImage: product.image,
                size: supplierProduct.size,
                sizeUnit: supplierProduct.sizeUnit,
                supplierId: supplierProduct.supplierId,
                supplierName: supplierProduct.supplierName,
                costPrice: supplierProduct.price,
                openPricing: supplierProduct.openPricing,
                plu: product.plu,
                quantity: quantity!,
                notes: notes,
                currentStock: currentStock!,
                approvalStatus: supplierProduct.requiresOrderApproval ? ApplicationApprovalStatus.AwaitingApproval : ApplicationApprovalStatus.Approved,
            };
            const insert = await dispatch(
                insertBasketItem({
                    basketId: basket.basket!.id,
                    basketItem: basketItem,
                })
            );
            if (insertBasketItem.fulfilled.match(insert)) {
                dispatch(showSuccess(`${product.name} added to basket`));

                resetInputs();
                if (addToBasketCallback) {
                    addToBasketCallback();
                }
            } else {
                setLoading(false);
            }

            submitInFlightRef.current = false;
        }
    };

    const resetInputs = () => {
        setCurrentStock(null);
        setQuantity(null);
        setErrors({
            quantity: null,
            currentStock: null,
        });
        setNotes("");
    }

    const handleQuantityChange = (value: number) => {
        const nextValue = Number.isFinite(value) ? value : null;
        setQuantity(nextValue);
        setErrors({ ...errors, quantity: getQuantityError(nextValue) });
    };

    const getQuantityError = (value: number | null): string | null => {
        const minimum = supplierProduct?.minimumOrderQuantity ?? -1;
        const isPallet = supplierProduct?.productOrderingType === "Pallets";
        const layerSize = supplierProduct?.layerSize;

        if (value == null || value <= -1) return "Enter a postive quantity";
        if (isPallet && layerSize && value % layerSize !== 0) {
            const lower = Math.floor(value / layerSize) * layerSize;
            const upper = Math.ceil(value / layerSize) * layerSize;
            return `Enter a complete-layer quantity. Valid quantities are multiples of ${layerSize} cases. The nearest valid quantities are ${lower || layerSize} and ${upper} cases.`;
        }
        if (minimum > 0 && value < minimum) {
            const layers = isPallet && layerSize ? ` (${minimum / layerSize} layers)` : "";
            return `The minimum order for this product is ${minimum} cases${layers}.`;
        }
        return null;
    };

    const handleQuantityInputChange = (value: number | null) => {
        setQuantity(value);
        setErrors({ ...errors, quantity: getQuantityError(value) });
    };

    /* Keep the raw number visible; validation is deliberately separate from normalization. */
    const validateQuantity = () => getQuantityError(quantity ?? null);

    const handleStockLevelChange = (value: number) => {
        setCurrentStock(value);
        setErrors({ ...errors, currentStock: requiredValidator(value) ?? minValueValidator(value, 0) });
    };

    if (!product) {

        if (loading) {
            return <></>;
        }
        return <Container>No Product</Container>
    }

    return (
        <Container>
            <ProductImage>
                <Image image={product.image}></Image>
            </ProductImage>
            <ProductDescription>{product.description}</ProductDescription>

            {(supplierProduct?.price || supplierProduct?.openPricing) && (
                <ProductDetails>
                    <ProductDetailLarge>
                        <span>Price</span>
                        {supplierProduct?.openPricing ?
                            <ProductDetailValue>TBC</ProductDetailValue>
                            :
                            <ProductDetailValue>{currencyFormatter.format(supplierProduct.price)}</ProductDetailValue>
                        }
                    </ProductDetailLarge>

                    <ProductDetailLarge>
                        <span>Cost per {supplierProduct.sizeUnit}</span>
                        {supplierProduct?.openPricing ?
                            <ProductDetailValue>TBC</ProductDetailValue>
                            :
                            <ProductDetailValue>{currencyFormatter.format(supplierProduct.price / supplierProduct.size)}</ProductDetailValue>
                        }
                    </ProductDetailLarge>
                    <ProductDetailLarge>
                        <span>Size:</span>
                        <ProductDetailValue>
                            {supplierProduct?.size} {supplierProduct?.sizeUnit}
                        </ProductDetailValue>
                    </ProductDetailLarge>

                </ProductDetails>
            )
            }

            <ProductDetails>
                {supplierProduct?.information && (
                    Object.entries(supplierProduct?.information).map(([key, value]) => {
                        return (
                            <ProductDetail key={key}>
                                <span>{key}:</span>
                                <ProductDetailValue>{value}</ProductDetailValue>
                            </ProductDetail>
                        );
                    })
                )}
                <ProductDetail>
                    <span>Plu:</span>
                    <ProductDetailValue>{product.plu}</ProductDetailValue>
                </ProductDetail>
                {supplierProduct?.origin && (
                    <ProductDetail>
                        <span>Origin:</span>
                        <ProductDetailValue>{supplierProduct?.origin}</ProductDetailValue>
                    </ProductDetail>
                )}

                {Object.entries(product.information).map(([key, value]) => {
                    return (
                        <ProductDetail key={key}>
                            <span>{key}:</span>
                            <ProductDetailValue>{value}</ProductDetailValue>
                        </ProductDetail>
                    );
                })}
            </ProductDetails>

            {
                supplierProduct && (
                    <>
                        {supplierProduct.productOrderingType === "Pallets" && supplierProduct.layerSize && supplierProduct.palletSize && (
                        <PalletInfo severity="info">
                                <div>{supplierProduct.layerSize} cases per layer</div>
                                <div>{supplierProduct.palletSize} layers per pallet</div>
                                <div>{supplierProduct.fullPalletQuantity ?? supplierProduct.layerSize * supplierProduct.palletSize} cases per full pallet</div>
                                {quantity != null && quantity > 0 && (
                                    <div>{quantity} cases = {quantity / supplierProduct.layerSize} layers = {palletFormatter(quantity / (supplierProduct.fullPalletQuantity ?? supplierProduct.layerSize * supplierProduct.palletSize))} pallets</div>
                                )}
                            </PalletInfo>
                        )}
                        <FeatureCard>
                            <FeatureCardTitle>Order</FeatureCardTitle>
                            <FeatureCardRow>
                                <FeatureCardColumn>
                                    <TextInput
                                        autoFocus
                                        type="number"
                                        ref={stockRef}
                                        id="txtStockLevel"
                                        label="Current Stock Level"
                                        value={currentStock?.toString() ?? ""}
                                        onChange={(event) => {
                                            handleStockLevelChange((event.target.value as unknown) as number)
                                        }}
                                        error={errors.currentStock}
                                    ></TextInput>
                                </FeatureCardColumn>
                                <FeatureCardColumn>
                                    <NumberInput
                                        id="txtQuantityInput"
                                        label="Quantity (cases)"
                                        value={quantity}
                                        step={supplierProduct.layerSize ?? 1}
                                        onChange={handleQuantityInputChange}
                                        onInputChange={(value) => handleQuantityInputChange(value === "" ? null : Number(value))}
                                        error={errors.quantity}
                                    />
                                </FeatureCardColumn>
                            </FeatureCardRow>
                            <FeatureCardRow>
                                <FeatureCardSingleColumn>
                                    <TextInput
                                        multiline={true}
                                        id="txtNotes"
                                        label="Notes"
                                        value={notes}
                                        onChange={(event) => setNotes(event.target.value)}
                                        ref={notesRef}
                                    />
                                </FeatureCardSingleColumn>
                            </FeatureCardRow>
                            <FeatureCardRow>
                                <FeatureCardSingleColumn>

                                    <Button
                                        onClick={basketItem != null ? updateBasket : addToBasket}
                                        label={addToBasketMessage}
                                        variant="contained"
                                        id="btnAddToBasket"
                                        fullWidth={true}
                                        disabled={loading || !!validateQuantity()}
                                    />

                                </FeatureCardSingleColumn>
                            </FeatureCardRow>
                        </FeatureCard>
                        <FeatureCard>
                            <FeatureCardTitle>Supplier</FeatureCardTitle>
                            <FeatureCardRow>
                                <FeatureCardColumn $border>
                                    <TextContainer $color="primary">{supplierProduct.supplierName}</TextContainer>
                                    {supplierProduct.openPricing ? <TextContainer $color="primary">TBC</TextContainer> :
                                        <TextContainer $color="primary">{currencyFormatter.format(supplierProduct.price / supplierProduct.size)} per {supplierProduct.sizeUnit}</TextContainer>
                                    }
                                    <TextContainer $color="primary"><strong>{supplierProduct.deliveryDay?.day} - {supplierProduct.deliveryDay?.date.toLocaleDateString("en-GB")} </strong></TextContainer>
                                </FeatureCardColumn>
                                {supplierProducts?.map(supplier => (
                                    <FeatureCardColumn key={`supplier_${supplier.id}`}>
                                        <TextContainer>{supplier.supplierName}</TextContainer>
                                        <TextContainer>{currencyFormatter.format(supplier.price! / supplier.size)} per {supplier.sizeUnit}</TextContainer>
                                        <TextContainer><strong>{supplier.deliveryDay?.day} - {supplier.deliveryDay?.date.toLocaleDateString("en-GB")} </strong></TextContainer>
                                    </FeatureCardColumn>
                                ))}
                            </FeatureCardRow>
                        </FeatureCard>

                    </>
                )
            }

            {
                (!supplierProduct && !loading) && (
                    <div>
                        <Alert severity="error">
                            There was an error with the product configuration, ensure size units are the same across all suppliers.
                        </Alert>
                        <Button
                            onClick={() => {
                                if (addToBasketCallback) {
                                    addToBasketCallback();
                                }
                            }}
                            label="Next"
                            variant="contained"
                            id="btnAddToBasket"
                            fullWidth={false}
                        />
                    </div>
                )
            }

        </Container >
    );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 2px;
`;


const ProductImage = styled.div`
  img {
    width: 100px;
    height: 100px;
  }
`;

const ProductDescription = styled.div`
  text-align: center;
  padding: 10px;
  border-bottom: 3px solid ${(props) => props.theme.application.scheme.primary};
`;

const PalletInfo = styled(Alert)`
    margin: 8px 0;
    width: auto;
`;

const ProductDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 4px;
  column-gap: 20px;
  row-gap: 5px;
  border: 1px dashed ${(props) => props.theme.application.border};
  padding: 10px;
  border-radius: 3px;
  justify-content: center;
    
`;

const ProductDetail = styled.div``;

const ProductDetailLarge = styled.div`
font-size:1.2crem;
`;
const ProductDetailValue = styled.span`
  margin-left: 5px;
  font-weight: 600;
`;



